import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence } from 'framer-motion'
import { ChevronRight, RotateCcw, Plus, Check, Settings } from 'lucide-react'
import { AutoHeight } from '@/src/renderer/components/ui/AutoHeight'
import { SUMMARY_KINDS, type PreviewPart, type PreviewTaskLine, type RestorePreviewModel, type SummaryKind } from '@/src/renderer/lib/backup/restorePreview'
import './RestorePreview.scss'

interface RestorePreviewProps {
  preview: RestorePreviewModel
}

/**
 * **Was das Wiederherstellen tun würde** (#158, Entscheidung 16), über dem Button und neu gerechnet
 * bei jedem Haken. „Schon da“ steht als Zahl obenan; „Kommen zurück“ und „Neu“ darunter klappen auf
 * und zeigen die Namen. Wer das falsche Backup erwischt hat, sieht es hier — ein Rückgängig gibt es
 * danach nicht.
 *
 * @example
 * <RestorePreview preview={buildRestorePreview(plan, device)} />
 */
export function RestorePreview({ preview }: RestorePreviewProps) {
  const { t } = useTranslation()
  if (preview.nothingToDo) {
    return <p className="restore-preview__nothing">{t('preferences.backups.preview.nothing')}</p>
  }
  return (
    <div className="restore-preview">
      {/* Zuerst, was bleibt — darunter die Gruppen, die sich aufklappen lassen. */}
      {preview.existing > 0 && (
        <p className="restore-preview__line">
          <Check size={14} strokeWidth={2.5} aria-hidden="true" className="restore-preview__icon" />
          {t('preferences.backups.preview.existing', { count: preview.existing })}
        </p>
      )}
      {preview.revived.total > 0 && <Group kind="revived" part={preview.revived} />}
      {preview.added.total > 0 && <Group kind="added" part={preview.added} />}
      {preview.skipped > 0 && (
        <p className="restore-preview__line restore-preview__line--muted">
          {t('preferences.backups.preview.skipped', { count: preview.skipped })}
        </p>
      )}
      {preview.overwritesSettings && (
        <p className="restore-preview__line">
          <Settings size={14} strokeWidth={2.5} aria-hidden="true" className="restore-preview__icon" />
          {t('preferences.backups.preview.settings')}
        </p>
      )}
    </div>
  )
}

/** „4 Projekte, 37 Aufgaben“ — nur Arten, die vorkommen. */
export function useSummaryText() {
  const { t } = useTranslation()
  return (counts: Record<SummaryKind, number>) => SUMMARY_KINDS
    .filter(kind => counts[kind] > 0)
    .map(kind => t(`preferences.backups.preview.kinds.${kind}`, { count: counts[kind] }))
    .join(', ')
}

function Group({ kind, part }: { kind: 'revived' | 'added'; part: PreviewPart }) {
  const { t } = useTranslation()
  const summary = useSummaryText()
  const [open, setOpen] = useState(false)
  const Icon = kind === 'revived' ? RotateCcw : Plus
  return (
    <div className={`restore-preview__group restore-preview__group--${kind}`}>
      <button type="button" className="restore-preview__toggle" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <ChevronRight size={14} strokeWidth={2.5} aria-hidden="true" className="restore-preview__chevron" />
        <Icon size={14} strokeWidth={2.5} aria-hidden="true" className="restore-preview__icon" />
        {t(`preferences.backups.preview.${kind}`, { summary: summary(part.counts) })}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <AutoHeight>
            <ul className="restore-preview__tree">
              {part.lifeSpaces.map(group => (
                <li key={group.lifeSpace} className="restore-preview__space">
                  <span className="restore-preview__space-name">{t(`lifeSpaces.${group.lifeSpace}`)}</span>
                  <ul className="restore-preview__items">
                    {group.projects.map(project => (
                      <li key={project.id}>
                        <span className="restore-preview__project">{project.name}</span>
                        <TaskLines lines={project.tasks} />
                      </li>
                    ))}
                    {group.quickNotes.length > 0 && (
                      <li>
                        <span className="restore-preview__project">{t('preferences.backups.tree.quickNotes')}</span>
                        <TaskLines lines={group.quickNotes} />
                      </li>
                    )}
                    {group.goals.length > 0 && (
                      <li>
                        <span className="restore-preview__project">{t('preferences.backups.tree.goals')}</span>
                        <TaskLines lines={group.goals.map((title, i) => ({ id: `g${i}`, title, depth: 0 }))} />
                      </li>
                    )}
                    {group.habits.length > 0 && (
                      <li>
                        <span className="restore-preview__project">{t('preferences.backups.tree.habits')}</span>
                        <TaskLines lines={group.habits.map((title, i) => ({ id: `h${i}`, title, depth: 0 }))} />
                      </li>
                    )}
                  </ul>
                </li>
              ))}
            </ul>
          </AutoHeight>
        )}
      </AnimatePresence>
    </div>
  )
}

function TaskLines({ lines }: { lines: PreviewTaskLine[] }) {
  if (lines.length === 0) return null
  return (
    <ul className="restore-preview__tasks">
      {lines.map(line => (
        <li key={line.id} className={`restore-preview__task restore-preview__task--depth-${Math.min(line.depth, 4)}`}>{line.title}</li>
      ))}
    </ul>
  )
}
